import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

/* ---------- styled components ---------- */
const PageBody = styled.div`
  height: calc(100vh - 64px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const SessionHeader = styled.div`
  padding: 0.75rem 1.5rem;
  border-bottom: 1px solid var(--border);
  background: var(--bg-surface);
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const BackLink = styled.a`
  color: var(--text-muted);
  font-size: 1.125rem;
  cursor: pointer;
`;

const HeaderInfo = styled.div`
  h2 {
    font-size: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`;

const SessionSub = styled.div`
  font-size: 0.75rem;
  color: var(--text-muted);
`;

const Badge = styled.span<{ $variant?: string }>`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.625rem;
  border-radius: 999px;
  font-size: 0.6875rem;
  font-weight: 500;
  background: ${({ $variant }) => $variant === 'green' ? 'var(--success-subtle)' : 'var(--error-subtle)'};
  color: ${({ $variant }) => $variant === 'green' ? 'var(--success)' : 'var(--error)'};
`;

const Btn = styled.button<{ $variant?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: ${({ $variant }) => $variant === 'secondary' ? '1px solid var(--border)' : 'none'};
  background: ${({ $variant }) => $variant === 'secondary' ? 'var(--bg-elevated)' : 'transparent'};
  color: var(--text-secondary);

  &:hover { background: var(--bg-elevated); }
`;

/* AI Summary Banner */
const AISummaryBanner = styled.div`
  padding: 0.875rem 1.5rem;
  background: var(--accent-subtle);
  border-bottom: 1px solid var(--accent-muted);
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
`;

const AISummaryIcon = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: var(--accent);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  flex-shrink: 0;
  margin-top: 0.125rem;
`;

const AISummaryText = styled.div`
  font-size: 0.8125rem;
  color: var(--accent-text);
  line-height: 1.5;
`;

const AISummaryLabel = styled.span`
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.6875rem;
  letter-spacing: 0.04em;
  display: block;
  margin-bottom: 0.25rem;
`;

/* Three-lane layout */
const ReplayLayout = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  overflow: hidden;

  @media (max-width: 1024px) { grid-template-columns: 1fr 1fr; }
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const Lane = styled.div<{ $hidden?: boolean }>`
  display: flex;
  flex-direction: column;
  border-right: 1px solid var(--border);
  overflow: hidden;

  &:last-child { border-right: none; }

  @media (max-width: 768px) {
    display: ${({ $hidden }) => $hidden ? 'none' : 'flex'};
  }
`;

const LaneHeader = styled.div`
  padding: 0.625rem 1rem;
  border-bottom: 1px solid var(--border);
  background: var(--bg-surface);
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-muted);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
`;

const LaneContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
`;

/* Conversation messages */
const ConvMsg = styled.div<{ $role: 'user' | 'agent' }>`
  display: flex;
  gap: 0.625rem;
  margin-bottom: 1.25rem;
  ${({ $role }) => $role === 'user' ? 'flex-direction: row-reverse;' : ''}
`;

const ConvAvatar = styled.div<{ $role: 'user' | 'agent'; $color?: string }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.625rem;
  font-weight: 600;
  flex-shrink: 0;
  background: ${({ $role, $color }) => $color || ($role === 'agent' ? 'var(--accent-subtle)' : 'var(--bg-elevated)')};
  color: ${({ $role, $color }) => $color ? 'var(--warning)' : ($role === 'agent' ? 'var(--accent)' : 'var(--text-secondary)')};
`;

const ConvBubble = styled.div<{ $role: 'user' | 'agent'; $delegated?: boolean }>`
  padding: 0.75rem 1rem;
  border-radius: 12px;
  font-size: 0.8125rem;
  line-height: 1.6;
  max-width: 90%;
  ${({ $role }) => $role === 'agent'
    ? 'background: var(--bg-elevated); color: var(--text-secondary); border-top-left-radius: 4px;'
    : 'background: var(--accent); color: white; border-top-right-radius: 4px;'}
  ${({ $delegated }) => $delegated ? 'border-left: 3px solid var(--warning);' : ''}
`;

const ConvTime = styled.div`
  font-size: 0.6875rem;
  color: var(--text-muted);
  margin-top: 0.25rem;
`;

const DelegationLabel = styled.div`
  font-size: 0.6875rem;
  color: var(--warning);
  font-weight: 500;
  margin-bottom: 0.25rem;
`;

/* Thinking items */
const ThoughtItem = styled.div`
  margin-bottom: 1.25rem;
  padding: 0.875rem;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  font-size: 0.8125rem;
`;

const ThoughtStep = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-muted);
  margin-bottom: 0.5rem;
`;

const StepNum = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--accent-subtle);
  color: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.625rem;
  font-weight: 700;
`;

const ThoughtText = styled.div`
  color: var(--text-secondary);
  line-height: 1.5;
`;

const ThoughtContext = styled.div`
  margin-top: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: var(--code-bg);
  border-radius: 6px;
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 0.75rem;
  color: var(--text-muted);
  max-height: 100px;
  overflow-y: auto;
  white-space: pre-wrap;
`;

/* Action items */
const ActionItem = styled.div`
  margin-bottom: 1rem;
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
`;

const ActionIcon = styled.div<{ $type: string }>`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  flex-shrink: 0;
  background: ${({ $type }) =>
    $type === 'delegation' ? 'var(--info-subtle)'
    : $type === 'task' ? 'var(--warning-subtle)'
    : $type === 'success' ? 'var(--success-subtle)'
    : $type === 'context' ? 'var(--accent-subtle)'
    : 'var(--error-subtle)'};
  color: ${({ $type }) =>
    $type === 'delegation' ? 'var(--info)'
    : $type === 'task' ? 'var(--warning)'
    : $type === 'success' ? 'var(--success)'
    : $type === 'context' ? 'var(--accent)'
    : 'var(--error)'};
`;

const ActionTitle = styled.div`
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 0.125rem;
`;

const ActionDesc = styled.div`
  font-size: 0.75rem;
  color: var(--text-muted);
  line-height: 1.4;
`;

const ActionTime = styled.div`
  font-size: 0.6875rem;
  color: var(--text-muted);
  margin-top: 0.25rem;
`;

const ActionData = styled.div`
  margin-top: 0.375rem;
  padding: 0.375rem 0.625rem;
  background: var(--code-bg);
  border-radius: 6px;
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 0.6875rem;
  color: var(--text-muted);
  white-space: pre-wrap;
`;

/* Mobile lane tabs */
const LaneTabs = styled.div`
  display: none;
  border-bottom: 1px solid var(--border);
  background: var(--bg-surface);

  @media (max-width: 768px) { display: flex; }
`;

const LaneTab = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 0.75rem;
  text-align: center;
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${({ $active }) => $active ? 'var(--accent)' : 'var(--text-muted)'};
  cursor: pointer;
  border: none;
  border-bottom: 2px solid ${({ $active }) => $active ? 'var(--accent)' : 'transparent'};
  background: none;
  font-family: inherit;
  transition: all 0.2s;
`;

type LaneName = 'conversation' | 'thinking' | 'actions';

/* ---------- component ---------- */
const SessionReplay: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeLane, setActiveLane] = useState<LaneName>('conversation');

  return (
    <PageBody>
      <SessionHeader>
        <HeaderLeft>
          <BackLink onClick={() => navigate('/sessions')} title="Back">&larr;</BackLink>
          <HeaderInfo>
            <h2>
              Emily &middot;{' '}
              <span style={{ fontFamily: "'SF Mono', monospace", color: 'var(--text-muted)', fontSize: '0.8125rem', fontWeight: 400 }}>
                #{id}
              </span>{' '}
              <Badge $variant="green">Success</Badge>
            </h2>
            <SessionSub>Feb 9, 2026 at 10:42 AM &middot; user-3847 &middot; 8 messages &middot; 2.3s total</SessionSub>
          </HeaderInfo>
        </HeaderLeft>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Btn>&#x1F504; Replay in Playground</Btn>
          <Btn $variant="secondary">&#x2B07; Export</Btn>
        </div>
      </SessionHeader>

      <AISummaryBanner>
        <AISummaryIcon>AI</AISummaryIcon>
        <AISummaryText>
          <AISummaryLabel>Session Summary</AISummaryLabel>
          Guest requested a table reservation for 4 people on Saturday evening. Emily identified this as a reservation query and delegated to Emily-Reservations. The task agent checked availability, found an open slot at 7:30 PM, and successfully completed the booking.
        </AISummaryText>
      </AISummaryBanner>

      <LaneTabs>
        {(['conversation', 'thinking', 'actions'] as LaneName[]).map((lane) => (
          <LaneTab key={lane} $active={activeLane === lane} onClick={() => setActiveLane(lane)}>
            {lane.charAt(0).toUpperCase() + lane.slice(1)}
          </LaneTab>
        ))}
      </LaneTabs>

      <ReplayLayout>
        {/* Conversation */}
        <Lane $hidden={activeLane !== 'conversation'}>
          <LaneHeader>&#x1F4AC; Conversation</LaneHeader>
          <LaneContent>
            <ConvMsg $role="user"><ConvAvatar $role="user">U</ConvAvatar><div><ConvBubble $role="user">Hi, I&apos;d like to book a table for 4 people this Saturday evening.</ConvBubble><ConvTime>10:42:01 AM</ConvTime></div></ConvMsg>
            <ConvMsg $role="agent"><ConvAvatar $role="agent">E</ConvAvatar><div><ConvBubble $role="agent">Welcome, valued guest! I&apos;d be happy to help you with a reservation. Let me check availability for Saturday evening for a party of 4.</ConvBubble><ConvTime>10:42:02 AM</ConvTime></div></ConvMsg>
            <ConvMsg $role="agent"><ConvAvatar $role="agent" $color="var(--warning-subtle)">ER</ConvAvatar><div><DelegationLabel>via Emily-Reservations</DelegationLabel><ConvBubble $role="agent" $delegated>I&apos;ve checked our availability. We have:<br /><br />&#x2022; 6:00 PM - Window table<br />&#x2022; 7:30 PM - Garden terrace<br />&#x2022; 8:45 PM - Main dining<br /><br />Which time would you prefer?</ConvBubble><ConvTime>10:42:03 AM</ConvTime></div></ConvMsg>
            <ConvMsg $role="user"><ConvAvatar $role="user">U</ConvAvatar><div><ConvBubble $role="user">The 7:30 PM garden terrace sounds wonderful!</ConvBubble><ConvTime>10:42:15 AM</ConvTime></div></ConvMsg>
            <ConvMsg $role="agent"><ConvAvatar $role="agent" $color="var(--warning-subtle)">ER</ConvAvatar><div><DelegationLabel>via Emily-Reservations</DelegationLabel><ConvBubble $role="agent" $delegated>Excellent! Confirmed:<br /><br />&#x2714; Saturday, Feb 14 at 7:30 PM<br />&#x2714; Party of 4<br />&#x2714; Garden terrace<br />&#x2714; Reservation #R-4821</ConvBubble><ConvTime>10:42:16 AM</ConvTime></div></ConvMsg>
            <ConvMsg $role="agent"><ConvAvatar $role="agent">E</ConvAvatar><div><ConvBubble $role="agent">Your reservation is all set! Is there anything else I can help you with?</ConvBubble><ConvTime>10:42:17 AM</ConvTime></div></ConvMsg>
            <ConvMsg $role="user"><ConvAvatar $role="user">U</ConvAvatar><div><ConvBubble $role="user">No, that&apos;s perfect. Thank you!</ConvBubble><ConvTime>10:42:25 AM</ConvTime></div></ConvMsg>
            <ConvMsg $role="agent"><ConvAvatar $role="agent">E</ConvAvatar><div><ConvBubble $role="agent">You&apos;re most welcome! We look forward to welcoming you Saturday evening. Have a wonderful day!</ConvBubble><ConvTime>10:42:26 AM</ConvTime></div></ConvMsg>
          </LaneContent>
        </Lane>

        {/* Thinking */}
        <Lane $hidden={activeLane !== 'thinking'}>
          <LaneHeader>&#x1F9E0; Agent Thinking</LaneHeader>
          <LaneContent>
            <ThoughtItem><ThoughtStep><StepNum>1</StepNum>Routing Decision</ThoughtStep><ThoughtText>User wants to book a table. Routing to <strong>emily-reservations</strong>.</ThoughtText><ThoughtContext>{`routing_prompt evaluation:\n"book a table" -> matches reservation pattern\nconfidence: 0.95\ndelegate_to: emily-reservations`}</ThoughtContext></ThoughtItem>
            <ThoughtItem><ThoughtStep><StepNum>2</StepNum>Context Assembly</ThoughtStep><ThoughtText>Loading conversation context. No prior sessions for this user.</ThoughtText><ThoughtContext>{`context_sources: [org_context]\nuser_context: null (new user)\nconversation_history: 1 message`}</ThoughtContext></ThoughtItem>
            <ThoughtItem><ThoughtStep><StepNum>3</StepNum>Task Execution Plan</ThoughtStep><ThoughtText>Planning to execute <strong>check_availability</strong> first, then <strong>make_reservation</strong>.</ThoughtText><ThoughtContext>{`task_plan:\n1. restaurant.check_availability\n   params: { date: "2026-02-14", party_size: 4 }\n2. restaurant.make_reservation (pending)`}</ThoughtContext></ThoughtItem>
            <ThoughtItem><ThoughtStep><StepNum>4</StepNum>Availability Check</ThoughtStep><ThoughtText>Task completed. Found 3 available slots.</ThoughtText><ThoughtContext>{`task_result: {\n  "status": "success",\n  "slots": [\n    { "time": "18:00", "location": "Window table" },\n    { "time": "19:30", "location": "Garden terrace" },\n    { "time": "20:45", "location": "Main dining" }\n  ]\n}`}</ThoughtContext></ThoughtItem>
            <ThoughtItem><ThoughtStep><StepNum>5</StepNum>Reservation Confirmed</ThoughtStep><ThoughtText>User selected 7:30 PM. Booking confirmed with ID R-4821.</ThoughtText><ThoughtContext>{`task_result: {\n  "reservation_id": "R-4821",\n  "date": "2026-02-14",\n  "time": "19:30",\n  "party_size": 4\n}`}</ThoughtContext></ThoughtItem>
            <ThoughtItem><ThoughtStep><StepNum>6</StepNum>Return to Emily</ThoughtStep><ThoughtText>Emily-Reservations completed. Control returned to Emily (management agent).</ThoughtText></ThoughtItem>
          </LaneContent>
        </Lane>

        {/* Actions */}
        <Lane $hidden={activeLane !== 'actions'}>
          <LaneHeader>&#x26A1; Actions &amp; Tasks</LaneHeader>
          <LaneContent>
            <ActionItem><ActionIcon $type="context">&#x1F4C1;</ActionIcon><div><ActionTitle>Session Created</ActionTitle><ActionDesc>New session initialized for emily agent</ActionDesc><ActionTime>10:42:01</ActionTime></div></ActionItem>
            <ActionItem><ActionIcon $type="delegation">&#x1F504;</ActionIcon><div><ActionTitle>Delegated to emily-reservations</ActionTitle><ActionDesc>Routing confidence: 95%</ActionDesc><ActionTime>10:42:02</ActionTime></div></ActionItem>
            <ActionItem><ActionIcon $type="task">&#x2699;</ActionIcon><div><ActionTitle>Task: check_availability</ActionTitle><ActionDesc>restaurant.check_availability executed</ActionDesc><ActionData>{`{ date: "2026-02-14", party_size: 4 }`}</ActionData><ActionTime>10:42:02 &mdash; 420ms</ActionTime></div></ActionItem>
            <ActionItem><ActionIcon $type="success">&#x2713;</ActionIcon><div><ActionTitle>Task Completed</ActionTitle><ActionDesc>3 available slots returned</ActionDesc><ActionTime>10:42:03</ActionTime></div></ActionItem>
            <ActionItem><ActionIcon $type="task">&#x2699;</ActionIcon><div><ActionTitle>Task: make_reservation</ActionTitle><ActionDesc>restaurant.make_reservation executed</ActionDesc><ActionData>{`{ time: "19:30", location: "Garden terrace" }`}</ActionData><ActionTime>10:42:16 &mdash; 380ms</ActionTime></div></ActionItem>
            <ActionItem><ActionIcon $type="success">&#x2713;</ActionIcon><div><ActionTitle>Reservation Confirmed</ActionTitle><ActionDesc>Booking R-4821 created</ActionDesc><ActionTime>10:42:16</ActionTime></div></ActionItem>
            <ActionItem><ActionIcon $type="delegation">&#x1F504;</ActionIcon><div><ActionTitle>Control returned to Emily</ActionTitle><ActionDesc>emily-reservations completed</ActionDesc><ActionTime>10:42:17</ActionTime></div></ActionItem>
            <ActionItem><ActionIcon $type="context">&#x1F4BE;</ActionIcon><div><ActionTitle>Session Saved</ActionTitle><ActionDesc>8 messages &middot; 2.3s total</ActionDesc><ActionTime>10:42:26</ActionTime></div></ActionItem>
          </LaneContent>
        </Lane>
      </ReplayLayout>
    </PageBody>
  );
};

export default SessionReplay;
